import { FormikProvider, useFormik } from "formik";
import { useCart, useShop, useUser } from "hooks";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clear } from "store/cart/actions";
import { API_ENDPOINTS, request } from "utilities";
import { loadScript, toJson } from "utilities/str";
import notification from "utilities/notification";
import AddressQuery from "./AddressQuery";
import { syncCart } from "store/cart/actions";

const OrderForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { items, subTotal, total, discount, coupon } = useCart();
  const { isUserLoggedIn, user } = useUser();
  const { info: shop } = useShop();
  const { data: addressList } = AddressQuery();
  const [payMethod, setPayMethod] = useState("");
  const [shippingCharge, setShippingCharge] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const [processing, setProcessing] = useState(false);
  const processingRef = useRef(false);

  // ── Razorpay config server se fetch karo ──────────────────────────
  // process.env use nahi karenge — server se aayega
  const [razorpayKey, setRazorpayKey] = useState(null);
  const [razorpayCurrency, setRazorpayCurrency] = useState("INR");

  useEffect(() => {
    const fetchRazorpayConfig = async () => {
      try {
        const res = await request.all(API_ENDPOINTS.RAZORPAY);
        if (res?.success && res?.data?.razorpay_key) {
          setRazorpayKey(res.data.razorpay_key);
          if (res.data.currency_code) {
            setRazorpayCurrency(res.data.currency_code);
          }
        } else {
          console.error("[Razorpay] Config fetch failed:", res);
        }
      } catch (err) {
        console.error("[Razorpay] Config fetch error:", err);
      }
    };

    fetchRazorpayConfig();
  }, []);

  // ── Address helpers ───────────────────────────────────────────────
  const updateSelectedAddress = (addressId) => {
    if (!addressList) return;
    if (addressId) {
      const address = addressList.find((addr) => addr.id === addressId);
      const parsedAddress = address ? toJson(address.address) : null;
      setSelectedAddressId(addressId);
      setSelectedAddress(parsedAddress ? { id: addressId, ...parsedAddress } : null);
    } else {
      setSelectedAddressId(null);
      setSelectedAddress(null);
    }
  };

  useEffect(() => {
    const storedAddressId = JSON.parse(localStorage.getItem("address"));
    updateSelectedAddress(storedAddressId);
  }, [addressList]);

  useEffect(() => {
    const handleAddressChange = (event) => {
      updateSelectedAddress(event.detail.addressId);
    };
    window.addEventListener("addressChanged", handleAddressChange);
    return () => window.removeEventListener("addressChanged", handleAddressChange);
  }, [addressList]);

  useEffect(() => {
    const checkLocalStorage = () => {
      const storedAddressId = JSON.parse(localStorage.getItem("address"));
      if (storedAddressId && storedAddressId !== selectedAddressId) {
        updateSelectedAddress(storedAddressId);
      }
    };
    const interval = setInterval(checkLocalStorage, 500);
    return () => clearInterval(interval);
  }, [selectedAddressId, addressList]);

  // ── Totals calculate karo ─────────────────────────────────────────
  const isDisabled = isUserLoggedIn && items.length > 0;

  useEffect(() => {
    let myTotal = total - discount;
    if (myTotal <= 0) myTotal = 0;

    let shippingCost = 0;
    if (shop.shipping_charges_enabled === 1) {
      if (shop.free_shipping_limit && myTotal >= shop.free_shipping_limit) {
        shippingCost = 0;
      } else {
        shippingCost = parseFloat(shop.shipping_charges) || 0;
      }
    }

    setShippingCharge(shippingCost);
    setGrandTotal(myTotal);
  }, [total, discount, shop]);

  const isCODAvailable =
    shop.cod_enabled === 1 &&
    (shop.cod_limit === null || grandTotal <= Number(shop.cod_limit));

  const codCharge =
    isCODAvailable && shop.cod_charges && payMethod === "CASH_ON_DELIVERY"
      ? Number(shop.cod_charges)
      : 0;

  const finalTotal = grandTotal + shippingCharge + codCharge;

  // ── Formik setup ──────────────────────────────────────────────────
  const initialValues = {
    products: items.map((item) => ({
      order_quantity: item?.quantity,
      product_id: item?.product_id,
      unit_price: item?.price,
      subtotal: item?.price * item?.quantity,
    })),
    status: 1,
    amount: total,
    shipping_charges: shippingCharge,
    paid_total: finalTotal,
    shop_id: shop?.id,
    total: finalTotal,
    discount,
    customer_contact: user?.contact || "0123456789",
    customer_id: user?.id,
    customer_email: user?.email,
    payment_gateway: payMethod,
    billing_address: selectedAddress || {},
    shipping_address: selectedAddress || {},
    billing_address_id: selectedAddressId || null,
    shipping_address_id: selectedAddressId || null,
    coupon_id: coupon?.id,
    referrer_name: "",
  };

  const onSubmit = async (values) => {
    dispatch(syncCart(items));

    const { referrer_name } = values;

    if (referrer_name === "") {
      notification("error", "Referrer name is required");
      return;
    }
    if (referrer_name.length < 3) {
      notification("error", "Referrer name must be minimum 3 characters");
      return;
    }
    if (!payMethod) {
      notification("error", "Please select a payment method");
      return;
    }
    if (
      !values.billing_address ||
      !values.shipping_address ||
      Object.keys(values.billing_address).length === 0 ||
      Object.keys(values.shipping_address).length === 0
    ) {
      notification("error", "Please select a billing and shipping address");
      return;
    }
    if (payMethod === "CASH_ON_DELIVERY" && !isCODAvailable) {
      notification("error", "Cash on Delivery is not available for this order");
      return;
    }

    if (payMethod === "razorPay") {
      // Razorpay key load hone ka wait karo
      if (!razorpayKey) {
        notification("error", "Payment config loading, please try again.");
        return;
      }
      await displayRazorpay(values);
    } else {
      request.post(API_ENDPOINTS.ORDER, values).then((response) => {
        if (response?.success) {
          notification("success", response?.message);
          dispatch(clear());
          navigate(`/`);
        } else {
          notification("error", response?.message);
        }
      });
    }
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit,
  });

  // ── Razorpay payment handler ──────────────────────────────────────
  async function displayRazorpay(order) {
    if (processingRef.current) {
      notification("error", "Payment already in progress");
      return;
    }

    processingRef.current = true;
    setProcessing(true);

    const resetProcessing = () => {
      processingRef.current = false;
      setProcessing(false);
    };

    try {
      // STEP 1: SDK load karo
      const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!res) {
        notification("error", "Razorpay SDK failed to load. Check internet connection.");
        resetProcessing();
        return;
      }

      // STEP 2: Backend pe order create karo
      const orderRes = await request.post(API_ENDPOINTS.ORDER, {
        ...order,
        payment_gateway: "razorPay",
        paymentStatus: "Pending",
      });

      if (!orderRes?.success) {
        notification("error", orderRes?.message || "Unable to initialize payment");
        resetProcessing();
        return;
      }

      const razorpayOrderId = orderRes.data.razorpay_order_id;
      if (!razorpayOrderId) {
        notification("error", "Payment order ID missing — please try again");
        resetProcessing();
        return;
      }

      // STEP 3: Amount paise mein
      const amountInPaise = Math.round(finalTotal * 100);

      const options = {
        key: razorpayKey,           // ✅ Server se fetch kiya — .env nahi
        amount: amountInPaise,
        currency: razorpayCurrency, // ✅ Server se fetch kiya
        name: "BEAUTE INDIA",
        description: "Order Payment",
        order_id: razorpayOrderId,

        // STEP 4: Payment success
        handler: async function (response) {
          try {
            const verifyRes = await request.post(API_ENDPOINTS.PAYMENT_VERIFY, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_signature:  response.razorpay_signature,
              amount:              amountInPaise,
              currency:            razorpayCurrency,
            });

            if (verifyRes?.success) {
              notification("success", "Payment successful! Order placed.");
              dispatch(clear());
              navigate("/");
            } else {
              notification(
                "error",
                verifyRes?.message || "Payment verification failed. Contact support."
              );
              resetProcessing();
            }
          } catch (e) {
            console.error("[Razorpay] Verify error:", e);
            notification("error", "Payment verification failed. Please contact support.");
            resetProcessing();
          }
        },

        // STEP 5: Modal dismiss
        modal: {
          ondismiss: function () {
            notification("error", "Payment cancelled.");
            resetProcessing();
          },
        },

        prefill: {
          name:    user?.name,
          email:   user?.email,
          contact: user?.contact,
        },

        theme: { color: "#000" },
      };

      const paymentObject = new window.Razorpay(options);

      // SDK-level errors (card decline, bank error)
      paymentObject.on("payment.failed", function (response) {
        console.error("[Razorpay] Payment failed:", response.error);
        notification(
          "error",
          response.error?.description || "Payment failed. Please try again."
        );
        resetProcessing();
      });

      paymentObject.open();

    } catch (err) {
      console.error("[Razorpay] Unexpected error:", err);
      notification("error", "Payment initialization failed. Please try again.");
      resetProcessing();
    }
  }

  // ── JSX ───────────────────────────────────────────────────────────
  return (
    <Fragment>
      <div className="mb-4">
        <label htmlFor="referrer_name" className="form-label">
          Referrer Name
        </label>
        <input
          type="text"
          id="referrer_name"
          name="referrer_name"
          className="form-control"
          value={formik.values.referrer_name}
          onChange={formik.handleChange}
          placeholder="Enter Referrer Name"
        />
      </div>

      <FormikProvider value={formik}>
        <div className="payment-option-section">
          <div className="row pt-5 pb-5">
            <div className="col-lg-8">
              <h2 className="payment">Payment Options</h2>

              <div className="choose-payment-mode mt-5 pe-5">
                <ul>
                  {/* Razorpay Option */}
                  <div className="radio-btn-section pe-3">
                    <li>
                      Razorpay
                      {!razorpayKey && (
                        <small style={{ color: "#999", marginLeft: 8 }}>
                          (loading...)
                        </small>
                      )}
                    </li>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="payment_gateway"
                        value="razorPay"
                        checked={payMethod === "razorPay"}
                        disabled={!razorpayKey}
                        onChange={(e) => setPayMethod(e.currentTarget.value)}
                      />
                    </div>
                  </div>

                  {isCODAvailable && (
                    <>
                      <hr />
                      <div className="radio-btn-section pe-3">
                        <li>Cash on Delivery</li>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="payment_gateway"
                            value="CASH_ON_DELIVERY"
                            checked={payMethod === "CASH_ON_DELIVERY"}
                            onChange={(e) => setPayMethod(e.currentTarget.value)}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <hr />
                </ul>
              </div>
            </div>

            {/* Order Summary */}
            <div className="col-lg-4">
              <div className="total-section">
                <h1 className="mb-5" style={{ fontSize: "26px" }}>
                  <u>Total</u>
                </h1>

                <div className="total-products">
                  {items.map((item, idx) => (
                    <div className="product-price" key={idx}>
                      <h5 style={{ fontSize: "18px" }}>{item?.model?.name}</h5>
                      <span style={{ fontSize: "18px" }}>
                        ₹ {item?.price * item?.quantity}
                      </span>
                    </div>
                  ))}

                  <div className="product-price">
                    <h5>Discount</h5>
                    <span>₹ {discount}</span>
                  </div>

                  <div className="product-price">
                    <h5>Sub Total</h5>
                    <span>₹ {grandTotal}</span>
                  </div>

                  <div className="product-price">
                    <h5>Shipping Charges</h5>
                    <span>₹ {shippingCharge}</span>
                  </div>

                  {isCODAvailable && payMethod === "CASH_ON_DELIVERY" && (
                    <div className="product-price">
                      <h5>COD Charges</h5>
                      <span>₹ {codCharge}</span>
                    </div>
                  )}

                  <div className="product-price">
                    <h5><strong>Final Total</strong></h5>
                    <span><strong>₹ {finalTotal}</strong></span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!isDisabled || processing}
                  onClick={formik.handleSubmit}
                  className="payment-btn mt-5"
                  style={{ opacity: processing ? 0.6 : 1 }}
                >
                  {processing ? "Processing..." : "Proceed to Payment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </FormikProvider>
    </Fragment>
  );
};

export default OrderForm;